import React, { useState, useEffect } from 'react';
import './App.css';
import api from './api';

import Login from './Login';
import Register from './Register';

import ProjectRoom from './components/ProjectRoom';
import Home from './components/Home';
import Profile from './components/Profile';
import CreateProject from './components/CreateProject';
import JoinProject from './components/JoinProject';

function App() {
    const [isLoggedIn, setIsLoggedIn] = useState(!!sessionStorage.getItem('token'));

    const [user, setUser] = useState(() => {
        const savedUser = sessionStorage.getItem('user');
        if (savedUser && savedUser !== "undefined") {
            return JSON.parse(savedUser);
        }
        return null;
    });

    const [showLogin, setShowLogin] = useState(true);
    const [activePage, setActivePage] = useState('home');
    const [selectedProject, setSelectedProject] = useState(null);
    
    // Store all projects (Created + Joined) fetched from backend
    const [allProjects, setAllProjects] = useState([]);

    // Fetch projects for the sidebar when logged in
    useEffect(() => {
        if (!isLoggedIn) return; 

        const fetchSidebarData = async () => {
            try {
                const token = sessionStorage.getItem('token');
                // The backend route now uses UNION/JOIN to find all your projects
                const res = await api.get('/api/projects/all-projects-list', {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                setAllProjects(res.data);
            } catch (err) {
                console.error("Sidebar fetch failed", err);
            }
        };
        fetchSidebarData();
    }, [isLoggedIn]);

    // Since the backend already filters by Creator OR Member, 
    // we use the data directly without a manual frontend filter.
    const mySidebarProjects = allProjects;

    const handleProjectClick = (project) => {
        setSelectedProject(project);
        setActivePage('project-room');
    };

    const handleProfilePicClick = () => {
        const input = document.getElementById('profileInput');
        if (input) input.click();
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('profilePic', file);

        try {
            const token = sessionStorage.getItem('token');
            const res = await api.put('/api/auth/profile-pic', formData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            const updatedUser = { ...user, profile_pic: res.data.profile_pic };
            setUser(updatedUser);
            sessionStorage.setItem('user', JSON.stringify(updatedUser));
            alert("Profile picture updated!");
        } catch (err) {
            console.error(err);
            alert("Upload failed.");
        }
    };

    const handleLoginSuccess = (userData) => {
        setIsLoggedIn(true);
        setUser(userData);
        sessionStorage.setItem('user', JSON.stringify(userData));
    };

    const handleLogout = () => {
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        setIsLoggedIn(false);
        setUser(null);
        setAllProjects([]); // Clear list on logout
        setActivePage('home'); 
    };

    if (isLoggedIn && !user) {
        return <div className="App"><h1>Loading User Data...</h1></div>;
    }

    if (!isLoggedIn) {
        return (
            <div className="App">
                <h1 className="app-logo-text" style={{ fontSize: '3rem', marginBottom: '20px' }}>Connect</h1>
                {showLogin ? (
                    <Login onLoginSuccess={handleLoginSuccess} />
                ) : (
                    <Register onRegisterSuccess={() => setShowLogin(true)} />
                )}
                <button
                    className="toggle-btn"
                    onClick={() => setShowLogin(!showLogin)}
                >
                    {showLogin ? "Need an account? Register" : "Already have an account? Login"}
                </button>
            </div>
        );
    }

    return (
        <div className="dashboard-wrapper">
            <aside className="right-sidebar">
                <div className="user-profile-card">
                    <div
                        className="avatar-placeholder"
                        onClick={handleProfilePicClick}
                        style={{ cursor: 'pointer', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                        {user?.profile_pic ? (
                            <img
                                src={`${user.profile_pic}`}
                                alt="Profile"
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                        ) : (
                            <span style={{ color: 'white', fontSize: '1.5rem', fontWeight: 'bold' }}>
                                {user?.username?.charAt(0).toUpperCase()}
                            </span>
                        )}
                    </div>

                    <input
                        type="file"
                        id="profileInput"
                        style={{ display: 'none' }}
                        accept="image/*"
                        onChange={handleFileChange}
                    />

                    <div className="user-meta">
                        <span className="user-name">{user?.username || "User"}</span>
                        <span className="user-status">● Online</span>
                    </div>
                    <button
                        className= {activePage === 'profile' ? 'profile-active' : 'profile-link'}
                        onClick={() => setActivePage('profile')}>Profile</button>

                    <button className="logout-link" onClick={handleLogout}>Logout</button>
                </div>

                <nav className="side-nav">
                    <button
                        className={activePage === 'home' ? 'nav-item active' : 'nav-item'}
                        onClick={() => setActivePage('home')}>Browse Projects</button>
                    <button
                        className={activePage === 'create-project' ? 'nav-item active' : 'nav-item'}
                        onClick={() => setActivePage('create-project')}>Create Project</button>

                    <button
                        className={activePage === 'join-project' ? 'nav-item active' : 'nav-item'}
                        onClick={() => setActivePage('join-project')}>Join Project with Code</button>
                </nav>

                {/* PROJECT SIDEBAR - SCROLLABLE LIST */}
                <div className="sidebar-projects-section">
                    <span className="sidebar-label">Your Projects</span>
                    <div className="sidebar-scroll-list">
                        {mySidebarProjects.length > 0 ? (
                            mySidebarProjects.map(proj => (
                                <div 
                                    key={proj.id} 
                                    className={selectedProject?.id === proj.id && activePage === 'project-room' ? "tiny-project-item active-proj" : "tiny-project-item"} 
                                    onClick={() => handleProjectClick(proj)}
                                >
                                    <img 
                                        src={`${proj.image_url}`} 
                                        alt="icon" 
                                        className="tiny-icon" 
                                        onError={(e) => { e.target.src = '/uploads/default-project.png' }} 
                                    />
                                    <span className="tiny-name">{proj.name}</span>
                                </div>
                            ))
                        ) : (
                            <p className="no-projects-text">No projects yet</p>
                        )}
                    </div>
                </div>
            </aside>

            <main className="main-content">
                <header className="content-header">
                    <div className="header-left">
                        <img src="/logo.png" alt="Connect Ideas Logo" className="app-logo-image" />
                        <h1 className="app-logo-text">Connect</h1>
                    </div>
                </header>

                <section className="page-container">
                    {activePage === 'home' && <Home user={user} onProjectClick={handleProjectClick} />}
                    {activePage === 'profile' && <Profile user={user} />}
                    {activePage === 'create-project' && <CreateProject user={user} />}
                    {activePage === 'join-project' && <JoinProject user={user} />}

                    {activePage === 'project-room' && selectedProject && (
                        <ProjectRoom project={selectedProject} user={user} />
                    )}
                </section>
            </main>
        </div>
    );
}

export default App;